import { ethers, Contract } from 'ethers'
import Web3 from 'web3'
import { JsonRpcProvider } from '@ethersproject/providers'
import IPancakePair from 'config/abi/IPancakePair.json' // Adjust the path as needed
import erc20ABI from 'config/abi/erc20.json' // Adjust the path as needed
import { FACTORY_ADDRESS, INIT_CODE_HASH } from '@icecreamswap/constants/src/common/swap'

async function getTokenData(provider: ethers.providers.Provider, tokenAddress: string) {
  const ERC20Contract = new Contract(tokenAddress, erc20ABI, provider)
  const [decimals, symbol, name] = await Promise.all([
    ERC20Contract.decimals(),
    ERC20Contract.symbol(),
    ERC20Contract.name(),
  ])
  return { address: tokenAddress, decimals, symbol, name }
}

export async function simulateSwap(tokenAddress: string) {
  try {
    const provider = new JsonRpcProvider('https://evm-rpc-ql1.foxxone.one')
    const web3 = new Web3('https://evm-rpc-ql1.foxxone.one')

    // Set the token addresses
    const qomAddress = '0xa26dfBF98Dd1A32FAe56A3D2B2D60A8a41b0bDF0' // WQOM address

    // Get the token data
    const qomToken = await getTokenData(provider, qomAddress)
    const swapToken = await getTokenData(provider, tokenAddress)

    // Sort the token addresses
    const [token0, token1] = [qomToken.address, swapToken.address].sort((a, b) =>
      a.toLowerCase().localeCompare(b.toLowerCase()),
    )

    // Compute the pair address
    const salt = ethers.utils.keccak256(ethers.utils.solidityPack(['address', 'address'], [token0, token1]))
    const pairAddress = ethers.utils.getCreate2Address(FACTORY_ADDRESS, salt, INIT_CODE_HASH)

    console.log(`Pair address computed: ${pairAddress}`)

    // Create a contract instance for the pair
    const pairContract = new Contract(pairAddress, IPancakePair, provider)

    // Fetch reserves from the pair contract
    const reserves = await pairContract.getReserves()
    console.log(`Reserves fetched: ${reserves}`)

    // Check if reserves are zero
    if (reserves[0].eq(0) && reserves[1].eq(0)) {
      throw new Error('No liquidity in the pool (reserves are zero).')
    }

    const reserve0 = reserves[0]
    const reserve1 = reserves[1]

    // Determine which reserve is which
    const token0Address = await pairContract.token0()
    const isQOMToken0 = token0Address.toLowerCase() === qomToken.address.toLowerCase()
    const reserveQOM = isQOMToken0 ? reserve0 : reserve1
    const reserveToken = isQOMToken0 ? reserve1 : reserve0

    // Ensure QOM is always the output token and swapToken is the input token
    const amountIn = ethers.utils.parseUnits('1', swapToken.decimals)
    const amountOut = reserveQOM.mul(amountIn).div(reserveToken.add(amountIn))

    console.log('Simulated swap results:', {
      swapTokenSymbol: swapToken.symbol,
      qomTokenSymbol: qomToken.symbol,
      reserveQOM: ethers.utils.formatUnits(reserveQOM, qomToken.decimals),
      reserveToken: ethers.utils.formatUnits(reserveToken, swapToken.decimals),
      amountOut: ethers.utils.formatUnits(amountOut, qomToken.decimals),
    })

    return {
      swapTokenSymbol: swapToken.symbol,
      qomTokenSymbol: qomToken.symbol,
      reserveQOM: ethers.utils.formatUnits(reserveQOM, qomToken.decimals),
      reserveToken: ethers.utils.formatUnits(reserveToken, swapToken.decimals),
      amountOut: ethers.utils.formatUnits(amountOut, qomToken.decimals),
    }
  } catch (error) {
    console.error('Error in simulateSwap:', error)
    throw error
  }
}
